import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Step,
  Stepper,
  useMediaQuery,
} from "@mui/material";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import ChartIcon from "@mui/icons-material/BarChart";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import SelectField from "Components/Common/SelectField";
import BaseTable from "Components/Common/BaseTable";
import Tooltip from "Components/Common/Tooltip";
import InputFieldText from "Components/Common/InputFieldText";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  POWER_CONSUMER_BILLING_INCHARGE,
  POWER_CONSUMER_BILLING_OPERATOR,
  powerAdmin1Roles,
  powerAdmin2Roles,
} from "common/constant";
import { getOptionLabel, getOptionValue } from "common/enums";
import SecurityDepositReviewModal from "./SecurityDepositReviewModal";
import Modal from "Components/Common/Modal";
import StepLabelField from "common/ApplicationApprovalTimelineChart/StepLabelField";

import {
  useLazyGetSecurityDepositChartQuery,
  useLazyGetSecurityDepositsQuery,
} from "apis/SecurityDeposit";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getImageUrl, isAuthorizedUser } from "utils";

const SecurityDeposit = ({ t }) => {
  const [billCycleId, setBillCycleId] = useState("");
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [consumerNumber, setConsumerNumber] = useState("");
  const [isDispatchBtnClicked, setIsDispatchBtnClicked] = useState(false);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [securityDepositIdForChart, setSecurityDepositIdForChart] =
    useState("");
  const { roles } = useSelector((state) => state.Login);
  const resetPageRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const billingCycleIdForSecurityDeposit = searchParams.get("billingCycleId");

  const isMobileDevice = useMediaQuery("(max-width: 768px)");
  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const [
    getSecurityDeposits,
    { data: { data: securityDepositData } = {}, isLoading },
  ] = useLazyGetSecurityDepositsQuery();

  const [
    getSecurityDepositApprovalData,
    {
      data: { data: securityDepositApprovalTrackingData = [] } = {},
      isLoading: isSecurityDepositApprovalTrackingDataLoading,
      isFetching: isSecurityDepositApprovalTrackingDataFetching,
    },
  ] = useLazyGetSecurityDepositChartQuery();

  const {
    data: { data: billingCycleOptions } = {},
    isLoading: isBillingCycleOptionsLoading,
  } = useGetBillingCycleOptionsQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const searchBtnHandler = async (e) => {
    e.preventDefault();
    await getSecurityDeposits(billCycleId, { consumerNumber });
    handleResetPage();
  };

  const clearBtnHandler = async () => {
    await getSecurityDeposits(billCycleId, {
      consumerNumber: "",
    });
    setConsumerNumber("");
    handleResetPage();
  };

  const updatedSecurityDepositOptionsOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Security Deposit")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Bill Number",
        field: "billNumber",
      },
      {
        title: "Payment Date",
        renderCell: ({ paymentDueDate }) =>
          paymentDueDate
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "--",
      },
      {
        title: "Security Deposit",
        renderCell: ({ netPayable }) => (
          <span className="text-nowrap"> &#8377; {netPayable}</span>
        ),
      },
      {
        title: "Status",
        renderCell: ({ status }) =>
          getOptionLabel("billCalculationStatus", status),
      },

      {
        title: "Actions",
        renderCell: ({ url, billNumber, id, approvedBy, revokedBy }) => (
          <Box minWidth="100px">
            <IconButton
              onClick={() =>
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Security Deposit",
                  documentType: "pdf",
                  documentName: `${billNumber}-security deposit`,
                })
              }
              disabled={!url}
            >
              <Tooltip title={t("View Security Deposit receipt")}>
                <ViewIcon
                  fontSize="small"
                  color={url ? "primary" : "disabled"}
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            {!!(approvedBy || revokedBy) && (
              <IconButton
                onClick={async () => {
                  try {
                    await getSecurityDepositApprovalData(id);
                    setSecurityDepositIdForChart(id);
                  } catch (error) {
                    console.error(error);
                  }
                }}
              >
                <Tooltip title={t("Approval History Timeline Chart")}>
                  <ChartIcon
                    fontSize="medium"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}
          </Box>
        ),
      },
    ],
    [securityDepositData]
  );

  useEffect(() => {
    (async () => {
      try {
        if (updatedSecurityDepositOptionsOptions?.length) {
          const id =
            updatedSecurityDepositOptionsOptions.find(
              ({ status }) =>
                status !== getOptionValue("billingStatus", "Closed")
            )?.value ??
            updatedSecurityDepositOptionsOptions.at(-1)?.value ??
            "";
          await getSecurityDeposits(id);
          setBillCycleId(id);
        }
      } catch (error) {}
    })();
  }, [updatedSecurityDepositOptionsOptions]);

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      {!!billingCycleIdForSecurityDeposit && (
        <SecurityDepositReviewModal
          handleClose={() => {
            searchParams.delete("billingCycleId");
            setSearchParams(searchParams);
            setIsDispatchBtnClicked(false);
          }}
          isDispatchModal={isDispatchBtnClicked}
        />
      )}

      <Backdrop
        open={
          isLoading ||
          isBillingCycleOptionsLoading ||
          isSecurityDepositApprovalTrackingDataLoading ||
          isSecurityDepositApprovalTrackingDataFetching
        }
      />

      {!!securityDepositIdForChart && (
        <Modal
          isOpen
          title="Approval Chart"
          maxWidth="lg"
          handleClose={() => setSecurityDepositIdForChart("")}
        >
          <Box sx={{ width: "100%" }}>
            <Stepper
              activeStep={securityDepositApprovalTrackingData.length}
              alternativeLabel={!isMobileDevice}
              className="mt-3"
              orientation={isMobileDevice ? "vertical" : "horizontal"}
            >
              {securityDepositApprovalTrackingData.map((step, index) => (
                <Step key={index}>
                  <StepLabelField step={step} />
                </Step>
              ))}
            </Stepper>
          </Box>
        </Modal>
      )}

      <BreadCrumb title={t("Security Deposit")} pageTitle={t("Billing")} />
      <form onSubmit={searchBtnHandler}>
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select bill cycle")}
              name="billCycleId"
              value={
                updatedSecurityDepositOptionsOptions?.find(
                  ({ value }) => value === billCycleId
                ) ?? ""
              }
              onChange={(_, { value }) => setBillCycleId(value)}
              options={updatedSecurityDepositOptionsOptions || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              placeholder={t("Enter consumer number")}
              type="number"
              value={consumerNumber}
              onChange={(e) => setConsumerNumber(e.target.value)}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            {!!securityDepositData?.length &&
              isAuthorizedUser(roles, [
                ...powerAdmin1Roles,
                ...powerAdmin2Roles,
                POWER_CONSUMER_BILLING_OPERATOR,
                POWER_CONSUMER_BILLING_INCHARGE,
              ]) && (
                <>
                  {isAuthorizedUser(roles, powerAdmin1Roles) && (
                    <Button
                      onClick={() => {
                        searchParams.set("billingCycleId", billCycleId);
                        setSearchParams(searchParams);
                        setIsDispatchBtnClicked(true);
                      }}
                      variant="contained"
                      sx={{ fontSize: "13px" }}
                      className="mb-2 me-2"
                    >
                      {t("Dispatch")}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      searchParams.set("billingCycleId", billCycleId);
                      setSearchParams(searchParams);
                    }}
                    sx={{ fontSize: "13px" }}
                    className="mb-2 me-2"
                    variant="contained"
                  >
                    {t("Review Bills")}
                  </Button>
                </>
              )}
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={securityDepositData}
        columns={columns}
        pageSize={10}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </>
  );
};

SecurityDeposit.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(SecurityDeposit);
